import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Lock, User, Loader2, ArrowLeft, HeartPulse } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";
import { api, formatApiError } from "@/lib/api";

export default function CustomerAuth() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const url = mode === "login" ? "/auth/customer/login" : "/auth/customer/register";
      const body = mode === "login" ? { phone, password } : { name, phone, password };
      const { data } = await api.post(url, body);
      toast.success(`Welcome${mode === "signup" ? "" : " back"}, ${data.name || "there"}!`);
      navigate("/account");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B192C] dot-grid spotlight grain flex items-center justify-center px-5 py-10" data-testid="customer-auth-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo /></div>
        <form onSubmit={submit} className="bg-white rounded-[1.75rem] p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]" data-testid="customer-auth-form">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#D92638]/10 flex items-center justify-center">
              <HeartPulse className="w-5 h-5 text-[#D92638]" />
            </div>
            <div>
              <h1 className="font-bold text-[#0B192C] text-lg">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
              <p className="text-xs text-slate-500">All your bookings & reports in one place</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-1.5 bg-slate-100 rounded-full p-1.5 mb-6" data-testid="auth-mode-toggle">
            {[["login", "Login"], ["signup", "Sign up"]].map(([id, label]) => (
              <button type="button" key={id} onClick={() => { setMode(id); setError(""); }} data-testid={`auth-mode-${id}`}
                className={`text-sm font-semibold py-2.5 rounded-full transition-all ${mode === id ? "bg-[#0B192C] text-white shadow" : "text-slate-500 hover:text-[#0B192C]"}`}>
                {label}
              </button>
            ))}
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl px-4 py-3" data-testid="customer-auth-error">{error}</div>
          )}

          {mode === "signup" && (
            <>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Full name</label>
              <div className="relative mt-1.5 mb-4">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input value={name} onChange={(e) => setName(e.target.value)} required={mode === "signup"} data-testid="customer-name-input"
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" placeholder="Your full name" />
              </div>
            </>
          )}

          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Phone number</label>
          <div className="relative mt-1.5 mb-4">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} inputMode="numeric" required data-testid="customer-phone-input"
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" placeholder="10-digit mobile number" />
          </div>

          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Password</label>
          <div className="relative mt-1.5">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="customer-password-input"
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" placeholder={mode === "signup" ? "Min 6 characters" : "Your password"} />
          </div>

          <button type="submit" disabled={loading} data-testid="customer-auth-submit"
            className="mt-6 w-full flex items-center justify-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white font-semibold py-3.5 rounded-full transition-all duration-200 active:scale-[0.98] disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Please wait..." : mode === "login" ? "Login" : "Create account"}
          </button>
          <div className="mt-5 text-center">
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#0D9488] transition-colors" data-testid="auth-back-home">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to hemozy.com
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
